/**
 * Hotspot block — frontend view script.
 *
 * Responsibilities:
 *  1. Populate each hotspot's marker <img> from the parent block's
 *     data-marker-url attribute; hide the fallback pin when the image loads.
 *  2. Wire up ARIA attributes (ids, aria-controls, aria-expanded) at runtime.
 *  3. Toggle the infobox open / closed when the marker button is clicked.
 *  4. Reposition the infobox so it never overflows the image boundaries.
 *  5. Close all open infoboxes when the user clicks outside them.
 */

import domReady from '@wordpress/dom-ready';

// ── Position helpers ──────────────────────────────────────────────────────────

/**
 * Remove any runtime position overrides from an infobox.
 *
 * @param {HTMLElement} infobox
 */
function resetInfobox( infobox ) {
	infobox.removeAttribute( 'data-infobox-pos' );
	infobox.style.removeProperty( '--infobox-x' );
}

/**
 * Adjust the infobox position so it stays within the image wrapper's bounds.
 *
 * Vertical  — if the default (above-marker) position would overflow the top
 *             edge, the infobox is flipped below the hotspot coords instead.
 * Horizontal — any remaining left/right overflow is corrected by shifting the
 *              box along the X axis via the --infobox-x CSS custom property.
 *              The CSS arrow counter-shifts by the same amount so it continues
 *              to point at the marker's centre.
 *
 * @param {HTMLElement} infobox
 * @param {HTMLElement} boundary  The .hotspot-image-wrapper element.
 */
function adjustInfobox( infobox, boundary ) {
	// Start from a clean slate so measurements are unaffected by old values.
	resetInfobox( infobox );

	const boxRect  = infobox.getBoundingClientRect();
	const wrapRect = boundary.getBoundingClientRect();

	// Vertical flip when the infobox would overflow the top edge of the image.
	if ( boxRect.top < wrapRect.top ) {
		infobox.setAttribute( 'data-infobox-pos', 'below' );
	}

	// Re-measure after the potential vertical flip.
	const adjRect = infobox.getBoundingClientRect();
	let shiftX = 0;

	if ( adjRect.left < wrapRect.left ) {
		shiftX = wrapRect.left - adjRect.left;
	} else if ( adjRect.right > wrapRect.right ) {
		shiftX = wrapRect.right - adjRect.right;
	}

	if ( shiftX !== 0 ) {
		infobox.style.setProperty( '--infobox-x', `${ shiftX }px` );
	}
}

// ── Open / close ──────────────────────────────────────────────────────────────

/**
 * Close an infobox and reset all related state.
 *
 * @param {HTMLElement} infobox
 */
function closeInfobox( infobox ) {
	infobox.hidden = true;
	resetInfobox( infobox );

	infobox
		.closest( '.wp-block-paula-hotspots-hotspot' )
		?.querySelector( '.hotspot-marker' )
		?.setAttribute( 'aria-expanded', 'false' );
}

// ── Initialisation ────────────────────────────────────────────────────────────

domReady( () => {
	const hotspots = document.querySelectorAll( '.wp-block-paula-hotspots-hotspot' );

	hotspots.forEach( ( hotspot, index ) => {
		const markerBtn = hotspot.querySelector( '.hotspot-marker' );
		const infobox   = hotspot.querySelector( '.hotspot-infobox' );
		const markerImg = hotspot.querySelector( '.hotspot-marker-img' );
		const markerPin = hotspot.querySelector( '.hotspot-marker-pin' );

		// The parent hotspot-image block exposes its marker URL on the wrapper.
		const imageBlock = hotspot.closest( '.wp-block-paula-hotspots-hotspot-image' );
		const wrapper    = imageBlock?.querySelector( '.hotspot-image-wrapper' );
		const markerUrl  = wrapper?.dataset?.markerUrl;

		// Populate the marker image; hide the fallback pin once the src is set.
		if ( markerImg && markerUrl ) {
			markerImg.src = markerUrl;
			if ( markerPin ) {
				markerPin.hidden = true;
			}
		}

		if ( ! markerBtn || ! infobox ) {
			return;
		}

		// Stable runtime IDs for ARIA relationships.
		const infoboxId = `paula-hotspot-infobox-${ index }`;
		infobox.id = infoboxId;
		markerBtn.setAttribute( 'aria-controls', infoboxId );
		markerBtn.setAttribute( 'aria-expanded', 'false' );

		markerBtn.addEventListener( 'click', ( event ) => {
			event.stopPropagation();

			if ( ! infobox.hidden ) {
				closeInfobox( infobox );
				return;
			}

			// Close every sibling infobox in the same image block first.
			imageBlock?.querySelectorAll( '.hotspot-infobox' ).forEach( ( sib ) => {
				if ( sib !== infobox ) {
					closeInfobox( sib );
				}
			} );

			infobox.hidden = false;
			markerBtn.setAttribute( 'aria-expanded', 'true' );

			// Adjust position now that the infobox is visible and measurable.
			if ( wrapper ) {
				adjustInfobox( infobox, wrapper );
			}
		} );

		// Clicks inside an open infobox must not bubble to the document
		// listener, which would close the box immediately.
		infobox.addEventListener( 'click', ( event ) => {
			event.stopPropagation();
		} );
	} );

	// Close all open infoboxes when the user clicks elsewhere on the page.
	document.addEventListener( 'click', () => {
		document
			.querySelectorAll( '.hotspot-infobox:not([hidden])' )
			.forEach( ( infobox ) => closeInfobox( infobox ) );
	} );
} );
